import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import Webcam from "react-webcam";
import { toast } from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../../components/BaseUrl";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";

const videoConstraints = {
    width: 400,
    height: 250,
    facingMode: "user",
};

const FaceScanRegistration = () => {
    const webcamRef = useRef(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
console.log("🔥 FaceScanRegistration component loaded");
    const getWorkerId = useCallback(() => {
        // First, try to get from navigation state (passed from UpdateInviteProfile)
        let workerId = localStorage.getItem("currentWorkerId") || localStorage.getItem("workerId");
  console.log("🚨 workerId from getWorkerId()", workerId);

        workerId = localStorage.getItem("currentWorkerId") || localStorage.getItem("workerId");
        if (workerId) {
            console.log("✅ Found worker_id from localStorage:", workerId);
            return workerId;
        }


        return null;
    }, []);
  

    // 🔄 Ensure fresh capture every time this page loads
    useEffect(() => {
        console.log("📸 FaceScanRegistration mounted");
        console.log("📝 Checking for worker_id (public endpoint - no token required)");

        const workerId = getWorkerId();
        if (!workerId) {
            console.error("❌ Worker ID not found in any location!");
toast.error(t("faceScan.workerIdMissing"));            // setTimeout(() => navigate("/login"), 3000);
            return;
        }

        console.log("✅ Worker ID verified:", workerId);
        console.log("📸 FaceScanRegistration ready for face capture");
    }, [navigate, getWorkerId]);

    // ✅ Capture face image
    const capture = async () => {
        if (!webcamRef.current) {
toast.error(t("faceScan.cameraNotAccessible"));
            return;
        }

        const imageSrc = webcamRef.current.getScreenshot();
        if (!imageSrc) {
            toast.error("Failed to capture image");
            return;
        }

        setCapturedImage(imageSrc);
    };

    // ✅ Upload captured face to server
    const uploadFaceImage = async () => {
        if (!capturedImage) {
            toast.error("Please capture an image first");
            return;
        }

        try {
            setUploading(true);

            // Convert base64 → Blob
            const blob = await fetch(capturedImage).then((res) => res.blob());
            const formData = new FormData();
            formData.append("image", blob, "face.jpg");

            console.log("📤 Uploading face image to:", `${BASE_URL}/upload/image`);
console.log("📤 Sending image blob to API:", `${BASE_URL}/upload/image`);
console.log("📤 Blob details:", blob);
            const res = await fetch(`${BASE_URL}/upload/image`, {
                method: "POST",
                body: formData,
            });

            const rawText = await res.text();
            console.log("📥 Raw server response:", rawText);

            let data;
            try {
                data = JSON.parse(rawText);
            } catch (err) {
                toast.error("Invalid server response");
                console.error("❌ JSON parse failed:", err);
                setUploading(false);
                return;
            }

            if (!res.ok || data.error) {
                toast.error(data.message || "Image upload failed");
                console.error("❌ Upload failed:", data.message);
                setUploading(false);
                return;
            }

            if (data.data?.url) {
                const faceImageUrl = data.data.url;
                console.log("✅ Face image uploaded successfully:", faceImageUrl);

                // Now call PUT API to update face in attendance/registration
                await updateFaceInBackend(faceImageUrl);
            } else {
                toast.error("No image URL returned from server");
                setUploading(false);
            }
        } catch (err) {
            console.error("❌ Upload error:", err);
            toast.error("Upload failed. Check console for details.");
            setUploading(false);
        }
    };

    // ✅ Update face scan in backend using public endpoint
    const updateFaceInBackend = async (faceImageUrl) => {
        try {
            setIsLoading(true);

            const workerId = getWorkerId();
            if (!workerId) {
                toast.error("Worker ID not found. Please restart registration.");
                setIsLoading(false);
                setUploading(false);
                return;
            }

            console.log("📤 Calling POST /attendance/public/update-face-by-id");
            console.log("🆔 Worker ID:", workerId);
            console.log("📸 Face image URL:", faceImageUrl);

            const res = await fetch(`${BASE_URL}/attendance/public/update-face-by-id`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    worker_id: workerId,
                    face_image_url: faceImageUrl,
                    validate_quality: true,
                }),
            });

            console.log("📡 Sending to update-face API:", {
    worker_id: workerId,
    face_image_url: faceImageUrl,
    validate_quality: true
});

            const rawText = await res.text();
            console.log("📥 Raw server response:", rawText);

            let data;
            try {
                data = JSON.parse(rawText);
            } catch (err) {
                console.error("❌ JSON parse failed:", err);
                toast.error("Invalid server response");
                setIsLoading(false);
                setUploading(false);
                return;
            }

            if (!res.ok || data.error) {
                toast.error(data.message || "Failed to update face scan");
                console.error("❌ Update failed:", data.message);
                setIsLoading(false);
                setUploading(false);
                return;
            }

            console.log("✅ Face scan updated successfully:", data);
         toast.success(t("faceScan.faceScanSuccess"));

            // Navigate to SetPassword after successful update
            setTimeout(() => {
              navigate("/set-password", {
                state: {
                  email: data.data.email,
                },
                replace: true,
              });
            }, 1500);
        } catch (err) {
            console.error("❌ API error:", err);
            toast.error("Error while updating face scan");
            setIsLoading(false);
            setUploading(false);
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: "#EFF4F8",
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: 2,
            }}
        >
            <Box
                sx={{
                    bgcolor: "white",
                    p: 3,
                    borderRadius: 2,
                    maxWidth: 600,
                    width: "100%",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    position: "relative",
                }}
            >
                {/* Close button */}
                <IconButton
                    onClick={() => navigate("/create-profile")}
                    sx={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        color: "#333",
                    }}
                >
                    <CloseRoundedIcon />
                </IconButton>

                {/* Title */}
                <Typography
                    sx={{
                        color: "#2C384C",
                        fontSize: "26px",
                        fontWeight: 600,
                        textAlign: "center",
                        fontFamily: "Poppins, sans-serif",
                        mb: 1,
                    }}
                >
                    {t("faceScanModal.title") || "Face Scan Capture"}
                </Typography>

                {/* Subtitle */}
                <p
                    style={{
                        fontSize: "14px",
                        color: "#8B8D97",
                        textAlign: "center",
                        fontFamily: "Poppins, sans-serif",
                        marginBottom: "20px",
                    }}
                >
                    {t("faceScanModal.subtitle") || "Please capture a clear photo of your face"}
                </p>

                {/* Camera/Image Display */}
                {!capturedImage ? (
                    <Webcam
                        audio={false}
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={videoConstraints}
                        style={{
                            width: "100%",
                            borderRadius: 8,
                            border: "2px solid #E0E0E0",
                            marginBottom: "20px",
                        }}
                    />
                ) : (
                    <img
                        src={capturedImage}
                        alt="Captured"
                        style={{
                            width: "100%",
                            borderRadius: 8,
                            border: "2px solid #E0E0E0",
                            marginBottom: "20px",
                        }}
                    />
                )}

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "center", alignItems: "center" }}>
                    {!capturedImage ? (
                        <Button
                            variant="outlined"
                            onClick={capture}
                            disabled={uploading || isLoading}
                            sx={{
                                fontSize: 15,
                                textTransform: "none",
                                borderRadius: "5px",
                                fontFamily: "Poppins, sans-serif",
                                color: "#006EC2",
                                border: "2px solid #006EC2",
                                px: 3,
                                py: 1,
                                height: "42px",
                            }}
                        >
                            {t("faceScanModal.capture") || "Capture"}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            onClick={() => setCapturedImage(null)}
                            disabled={uploading || isLoading}
                            sx={{
                                fontSize: 15,
                                textTransform: "none",
                                borderRadius: "5px",
                                fontFamily: "Poppins, sans-serif",
                                color: "#006EC2",
                                border: "2px solid #006EC2",
                                px: 3,
                                py: 1,
                                height: "42px",
                            }}
                        >
                            {t("faceScanModal.retake") || "Retake"}
                        </Button>
                    )}

                    {/* Continue Button */}
                    <Button
                        variant="contained"
                        disabled={!capturedImage || uploading || isLoading}
                        onClick={uploadFaceImage}
                        sx={{
                            fontSize: 15,
                            textTransform: "none",
                            borderRadius: "5px",
                            fontFamily: "Poppins, sans-serif",
                            backgroundColor: "#006EC2",
                            color: "#fff",
                            px: 4,
                            py: 1,
                            height: "42px",
                            position: "relative",
                            "&:hover": {
                                backgroundColor: "#005bb5",
                            },
                        }}
                    >
                        {uploading || isLoading ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                                {uploading ? "Uploading..." : "Processing..."}
                            </Box>
                        ) : (
                            t("faceScanModal.continue") || "Continue"
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default FaceScanRegistration;
